(() => {
  if (typeof Shopify !== 'undefined' && Shopify.designMode) return;

  const FADE_DURATION = 300;
  const pageCache = new Map();
  let activeController = null;
  let isTransitioning = false;
  let originSaved = false;

  const mainContent = () => document.getElementById('MainContent');

  const handleClick = (event) => {
    const backBtn = event.target.closest('.page-transition-back');
    if (backBtn) {
      event.preventDefault();
      history.back();
      return;
    }

    const link = event.target.closest('[data-page-transition] a');
    if (!link) return;

    const href = link.href;
    if (!href || href === window.location.href) return;

    event.preventDefault();

    if (!originSaved) {
      pageCache.set(window.location.href, mainContent().innerHTML);
      titleCache.set(window.location.href, document.title);
      history.replaceState(
        { pageTransition: true, url: window.location.href, isOrigin: true },
        '',
        window.location.href
      );
      originSaved = true;
    }

    link.closest('.card-grid-item')?.classList.add('card-grid-item--clicked');
    handleTransition(href, false);
  };

  const getPage = async (url, signal) => {
    const cached = pageCache.get(url);
    if (cached) {
      const container = document.createElement('div');
      container.innerHTML = cached;
      document.title = titleCache.get(url) || document.title;
      return container;
    }

    const res = await fetch(url, { signal });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const html = await res.text();
    const result = extractMainContent(html);
    if (!result) throw new Error('No #MainContent found');

    pageCache.set(url, result.container.innerHTML);
    titleCache.set(url, result.title);
    document.title = result.title;
    return result.container;
  };

  const handleTransition = async (url, isBack) => {
    if (isTransitioning) {
      activeController?.abort();
    }

    isTransitioning = true;
    activeController = new AbortController();
    const { signal } = activeController;

    try {
      if (document.startViewTransition) {
        const newContent = await getPage(url, signal);
        await preloadStylesheets(newContent);

        document.startViewTransition(() => {
          swapContent(newContent, !isBack);
          window.scrollTo({ top: 0, behavior: 'instant' });
        });
      } else {
        const cached = pageCache.has(url);
        const pagePromise = getPage(url, signal);

        if (!cached) await fadeOut(signal);
        const newContent = await pagePromise;

        await preloadStylesheets(newContent);
        if (cached) await fadeOut(signal);

        swapContent(newContent, !isBack);
        window.scrollTo({ top: 0, behavior: 'instant' });
        await fadeIn();
      }

      if (!isBack) {
        history.pushState({ pageTransition: true, url }, '', url);
      }
      reinitAnimations();
    } catch (err) {
      if (err.name === 'AbortError') return;
      window.location.href = url;
    } finally {
      isTransitioning = false;
      activeController = null;
    }
  };

  const titleCache = new Map();

  const extractMainContent = (html) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const newMain = doc.getElementById('MainContent');
    if (!newMain) return null;

    const container = document.createElement('div');
    container.innerHTML = newMain.innerHTML;

    const titleEl = doc.querySelector('title');
    const title = titleEl ? titleEl.textContent : '';

    return { container, title };
  };

  const preloadStylesheets = (container) => {
    const links = container.querySelectorAll('link[rel="stylesheet"]');
    const existing = new Set(
      Array.from(document.querySelectorAll('link[rel="stylesheet"]')).map((l) => l.href)
    );

    const loads = [];
    for (const link of links) {
      if (existing.has(link.href)) continue;
      const clone = link.cloneNode();
      loads.push(
        new Promise((resolve) => {
          clone.onload = resolve;
          clone.onerror = resolve;
        })
      );
      document.head.appendChild(clone);
    }

    return loads.length > 0 ? Promise.all(loads) : Promise.resolve();
  };

  const injectBackButton = () => {
    const el = mainContent();
    if (!el) return;

    const btn = document.createElement('button');
    btn.className = 'page-transition-back';
    btn.setAttribute('aria-label', 'Go back');
    btn.innerHTML = `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`;

    el.prepend(btn);
  };

  const swapContent = (container, showBack) => {
    const el = mainContent();
    if (!el) return;
    el.innerHTML = container.innerHTML;
    el.classList.remove('main-content-fade-out', 'main-content-fade-in');
    if (showBack) injectBackButton();
  };

  const fadeOut = (signal) => {
    return new Promise((resolve) => {
      const el = mainContent();
      if (!el) return resolve();

      const onEnd = () => {
        el.removeEventListener('transitionend', onEnd);
        resolve();
      };

      if (signal?.aborted) return resolve();
      el.addEventListener('transitionend', onEnd);
      el.classList.add('main-content-fade-out');

      setTimeout(resolve, FADE_DURATION + 50);
    });
  };

  const fadeIn = () => {
    return new Promise((resolve) => {
      const el = mainContent();
      if (!el) return resolve();

      el.classList.remove('main-content-fade-out');
      el.offsetHeight;
      el.classList.add('main-content-fade-in');

      const onEnd = () => {
        el.removeEventListener('transitionend', onEnd);
        el.classList.remove('main-content-fade-in');
        resolve();
      };

      el.addEventListener('transitionend', onEnd);
      setTimeout(resolve, FADE_DURATION + 50);
    });
  };

  const reinitAnimations = () => {
    if (typeof initializeScrollAnimationTrigger === 'function') {
      initializeScrollAnimationTrigger(mainContent());
    }
    document.dispatchEvent(new CustomEvent('page:transition'));
  };

  const prefetchPage = async (url) => {
    if (pageCache.has(url)) return;
    try {
      const res = await fetch(url);
      if (!res.ok) return;
      const html = await res.text();
      const result = extractMainContent(html);
      if (!result) return;
      pageCache.set(url, result.container.innerHTML);
      titleCache.set(url, result.title);
    } catch {
      // silently skip failed prefetches
    }
  };

  const idleCallback = typeof requestIdleCallback === 'function'
    ? requestIdleCallback
    : (cb) => setTimeout(cb, 1);

  const initPrefetch = () => {
    const observer = new IntersectionObserver(
      (entries, obs) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          obs.unobserve(entry.target);

          const hrefs = Array.from(
            entry.target.querySelectorAll('a[href]')
          )
            .map((a) => a.href)
            .filter((href) => href && href !== window.location.href && !pageCache.has(href));

          const unique = [...new Set(hrefs)];
          if (unique.length === 0) return;

          idleCallback(
            () => { Promise.all(unique.map(prefetchPage)); },
            { timeout: 3000 }
          );
        }
      },
      { rootMargin: '200px' }
    );

    for (const el of document.querySelectorAll('[data-page-transition][data-prefetch]')) {
      observer.observe(el);
    }
  };

  window.addEventListener('popstate', (event) => {
    if (!event.state?.pageTransition) return;
    handleTransition(window.location.href, event.state.isOrigin === true);
  });

  document.addEventListener('click', handleClick);
  initPrefetch();
})();
